import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, CreditCard, X, Plus, Loader2, Pencil, Trash2, Link } from 'lucide-react';
import { HitApi } from '../../../Api/ApiHit';
import { searchKiosk, searchPaymentMethod, addPaymentMethod, updatePaymentMethod, deletePaymentMethod } from '../../../constant/Constant';
import PageHeader from '../../../PageHeader/PageHeader';

function KiosQuicksale() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [kiosk, setKiosk] = useState(null);

    // Payment Methods Modal Statessdfsdfsdfsf
    const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
    const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);
    const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);

    // Add Payment Method UPI Fields
    const [newPaymentMethodName, setNewPaymentMethodName] = useState('');
    const [newUpiId, setNewUpiId] = useState('');
    const [newPayeeName, setNewPayeeName] = useState('');
    const [newDefaultAmount, setNewDefaultAmount] = useState('');
    const [newCurrency, setNewCurrency] = useState('INR');
    const [newTransactionNote, setNewTransactionNote] = useState('');

    // Edit Payment Method States
    const [showEditPaymentMethodModal, setShowEditPaymentMethodModal] = useState(false);
    const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
    const [editPaymentMethodName, setEditPaymentMethodName] = useState('');
    const [editPaymentMethodStatus, setEditPaymentMethodStatus] = useState('active');
    const [editUpiId, setEditUpiId] = useState('');
    const [editPayeeName, setEditPayeeName] = useState('');
    const [editDefaultAmount, setEditDefaultAmount] = useState('');
    const [editCurrency, setEditCurrency] = useState('INR');
    const [editTransactionNote, setEditTransactionNote] = useState('');
    const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

    // Generate UPI deep link
    const generateUpiLink = (upiId, payeeName, amount, currency, transactionNote) => {
        if (!upiId) return null;
        const params = new URLSearchParams();
        params.set('pa', upiId);
        if (payeeName) params.set('pn', payeeName);
        if (amount) params.set('am', amount);
        if (currency) params.set('cu', currency);
        if (transactionNote) params.set('tn', transactionNote);
        return `upi://pay?${params.toString()}`;
    };

    useEffect(() => {
        fetchKioskDetails();
    }, [id]);

    const fetchKioskDetails = async () => {
        try {
            setLoading(true);
            const requestPayload = {
                page: 1,
                limit: 1,
                search: {
                    _id: id,
                    isActive: true
                }
            };

            const response = await HitApi(requestPayload, searchKiosk);

            if (response.success && response.data && response.data.length > 0) {
                setKiosk(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching kiosk details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoadingPaymentMethods(true);
            const requestPayload = {
                page: 1,
                limit: 50,
                search: {}
            };

            const response = await HitApi(requestPayload, searchPaymentMethod);

            if (response.statusCode === 200 && response.data?.docs) {
                setPaymentMethods(response.data.docs);
            } else {
                setPaymentMethods([]);
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setPaymentMethods([]);
        } finally {
            setLoadingPaymentMethods(false);
        }
    };

    const handlePaymentMethodCardClick = () => {
        setShowPaymentMethodsModal(true);
        fetchPaymentMethods();
    };

    const resetAddForm = () => {
        setNewPaymentMethodName('');
        setNewUpiId('');
        setNewPayeeName('');
        setNewDefaultAmount('');
        setNewCurrency('INR');
        setNewTransactionNote('');
    };

    const handleAddPaymentMethod = async () => {
        if (!newPaymentMethodName.trim()) {
            alert('Please enter payment method name');
            return;
        }

        try {
            setIsAddingPaymentMethod(true);

            const upiLink = generateUpiLink(
                newUpiId.trim(),
                newPayeeName.trim(),
                newDefaultAmount,
                newCurrency,
                newTransactionNote.trim()
            );

            const payload = {
                name: newPaymentMethodName.trim(),
                upiId: newUpiId.trim() || null,
                payeeName: newPayeeName.trim() || null,
                defaultAmount: newDefaultAmount ? parseFloat(newDefaultAmount) : null,
                currency: newCurrency || 'INR',
                transactionNote: newTransactionNote.trim() || null,
                upiLink: upiLink,
                status: 'active'
            };

            const response = await HitApi(payload, addPaymentMethod);

            if (response.statusCode === 200 || response.statusCode === 201) {
                alert('Payment method added successfully');
                resetAddForm();
                setShowAddPaymentMethodModal(false);
                fetchPaymentMethods();
            } else {
                alert(response.message || 'Failed to add payment method');
            }
        } catch (error) {
            console.error('Error adding payment method:', error);
            alert('Failed to add payment method');
        } finally {
            setIsAddingPaymentMethod(false);
        }
    };

    const handleEditPaymentMethodClick = (method) => {
        setEditingPaymentMethod(method);
        setEditPaymentMethodName(method.name || '');
        setEditPaymentMethodStatus(method.status || 'active');
        setEditUpiId(method.upiId || '');
        setEditPayeeName(method.payeeName || '');
        setEditDefaultAmount(method.defaultAmount ? String(method.defaultAmount) : '');
        setEditCurrency(method.currency || 'INR');
        setEditTransactionNote(method.transactionNote || '');
        setShowEditPaymentMethodModal(true);
    };

    const handleUpdatePaymentMethod = async () => {
        if (!editPaymentMethodName.trim()) {
            alert('Please enter payment method name');
            return;
        }

        try {
            setIsUpdatingPaymentMethod(true);

            const upiLink = generateUpiLink(
                editUpiId.trim(),
                editPayeeName.trim(),
                editDefaultAmount,
                editCurrency,
                editTransactionNote.trim()
            );

            const payload = {
                _id: editingPaymentMethod._id,
                name: editPaymentMethodName.trim(),
                upiId: editUpiId.trim() || null,
                payeeName: editPayeeName.trim() || null,
                defaultAmount: editDefaultAmount ? parseFloat(editDefaultAmount) : null,
                currency: editCurrency || 'INR',
                transactionNote: editTransactionNote.trim() || null,
                upiLink: upiLink,
                status: editPaymentMethodStatus
            };

            const response = await HitApi(payload, updatePaymentMethod);

            if (response.statusCode === 200 || response.statusCode === 201) {
                alert('Payment method updated successfully');
                setShowEditPaymentMethodModal(false);
                setEditingPaymentMethod(null);
                setEditPaymentMethodName('');
                setEditPaymentMethodStatus('active');
                setEditUpiId('');
                setEditPayeeName('');
                setEditDefaultAmount('');
                setEditCurrency('INR');
                setEditTransactionNote('');
                fetchPaymentMethods();
            } else {
                alert(response.message || 'Failed to update payment method');
            }
        } catch (error) {
            console.error('Error updating payment method:', error);
            alert('Failed to update payment method');
        } finally {
            setIsUpdatingPaymentMethod(false);
        }
    };

    const handleDeletePaymentMethod = async (method) => {
        if (!window.confirm(`Are you sure you want to delete "${method.name}"?`)) {
            return;
        }

        try {
            const payload = {
                _id: method._id
            };

            const response = await HitApi(payload, deletePaymentMethod);

            if (response.statusCode === 200 || response.statusCode === 201) {
                alert('Payment method deleted successfully');
                fetchPaymentMethods();
            } else {
                alert(response.message || 'Failed to delete payment method');
            }
        } catch (error) {
            console.error('Error deleting payment method:', error);
            alert('Failed to delete payment method');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <ShoppingCart className="w-16 h-16 text-teal-600 animate-pulse mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-gray-900">
                            Kios Quicksale - {kiosk?.kioskName || 'Kiosk'}
                        </h1>
                    </div>
                    <button
                        onClick={() => navigate(`/kiosk-management/dashboard/${id}`)}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Dashboard
                    </button>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Kios Quicksale Card */}
                    <div
                        className="bg-white rounded-lg shadow-md p-8 cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-teal-500"
                        onClick={() => {/* Navigate to quicksale functionality */ }}
                    >
                        <div className="text-center py-8">
                            <ShoppingCart className="w-16 h-16 text-teal-600 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Kios Quicksale</h2>
                            <p className="text-gray-600 text-sm">Quick sale and billing functionality for kiosk customers.</p>
                        </div>
                    </div>

                    {/* Payment Methods Card */}
                    <div
                        className="bg-white rounded-lg shadow-md p-8 cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
                        onClick={handlePaymentMethodCardClick}
                    >
                        <div className="text-center py-8">
                            <CreditCard className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Methods</h2>
                            <p className="text-gray-600 text-sm">Manage payment methods for kiosk billing.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Methods Modal */}
            {showPaymentMethodsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CreditCard className="w-6 h-6 text-white" />
                                <h2 className="text-xl font-bold text-white">Payment Methods</h2>
                            </div>
                            <button
                                onClick={() => setShowPaymentMethodsModal(false)}
                                className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[50vh]">
                            {loadingPaymentMethods ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
                                    <p className="text-gray-600">Loading payment methods...</p>
                                </div>
                            ) : paymentMethods.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <CreditCard className="w-16 h-16 text-gray-300 mb-3" />
                                    <p className="text-gray-500">No payment methods found</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {paymentMethods.map((method, index) => (
                                        <div
                                            key={method._id || index}
                                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <CreditCard className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{method.name}</p>
                                                    {method.upiId && (
                                                        <p className="text-xs text-purple-600 flex items-center gap-1">
                                                            <Link className="w-3 h-3" />
                                                            UPI: {method.upiId}
                                                        </p>
                                                    )}
                                                    {method.payeeName && (
                                                        <p className="text-xs text-gray-500">{method.payeeName}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${method.status === 'active'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-600'
                                                    }`}>
                                                    {method.status || 'active'}
                                                </span>
                                                <button
                                                    onClick={() => handleEditPaymentMethodClick(method)}
                                                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePaymentMethod(method)}
                                                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="border-t px-6 py-4">
                            <button
                                onClick={() => setShowAddPaymentMethodModal(true)}
                                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                                Add Payment Method
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Payment Method Modal */}
            {showAddPaymentMethodModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Add Payment Method</h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                            <input
                                type="text"
                                placeholder="e.g. UPI, Cash, Card"
                                value={newPaymentMethodName}
                                onChange={(e) => setNewPaymentMethodName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">UPI ID (pa)</label>
                            <input
                                type="text"
                                placeholder="e.g. yourname@okhdfcbank"
                                value={newUpiId}
                                onChange={(e) => setNewUpiId(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Payee Name (pn)</label>
                            <input
                                type="text"
                                placeholder="e.g. Shariq Ansari"
                                value={newPayeeName}
                                onChange={(e) => setNewPayeeName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Default Amount (am)</label>
                                <input
                                    type="number"
                                    placeholder="e.g. 500"
                                    value={newDefaultAmount}
                                    onChange={(e) => setNewDefaultAmount(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Currency (cu)</label>
                                <input
                                    type="text"
                                    placeholder="INR"
                                    value={newCurrency}
                                    onChange={(e) => setNewCurrency(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Note (tn)</label>
                            <input
                                type="text"
                                placeholder="e.g. Salon Payment"
                                value={newTransactionNote}
                                onChange={(e) => setNewTransactionNote(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        {/* UPI Link Preview */}
                        {newUpiId && (
                            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <p className="text-xs font-medium text-purple-700 mb-1">UPI Link Preview:</p>
                                <p className="text-xs text-purple-600 break-all">
                                    {generateUpiLink(newUpiId, newPayeeName, newDefaultAmount, newCurrency, newTransactionNote)}
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowAddPaymentMethodModal(false);
                                    resetAddForm();
                                }}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddPaymentMethod}
                                disabled={isAddingPaymentMethod}
                                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isAddingPaymentMethod ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Adding...
                                    </>
                                ) : (
                                    'Add'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Payment Method Modal */}
            {showEditPaymentMethodModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Payment Method</h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                            <input
                                type="text"
                                placeholder="Enter payment method name"
                                value={editPaymentMethodName}
                                onChange={(e) => setEditPaymentMethodName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                value={editPaymentMethodStatus}
                                onChange={(e) => setEditPaymentMethodStatus(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">UPI ID (pa)</label>
                            <input
                                type="text"
                                placeholder="e.g. yourname@okhdfcbank"
                                value={editUpiId}
                                onChange={(e) => setEditUpiId(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Payee Name (pn)</label>
                            <input
                                type="text"
                                placeholder="e.g. Shariq Ansari"
                                value={editPayeeName}
                                onChange={(e) => setEditPayeeName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Default Amount (am)</label>
                                <input
                                    type="number"
                                    placeholder="e.g. 500"
                                    value={editDefaultAmount}
                                    onChange={(e) => setEditDefaultAmount(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Currency (cu)</label>
                                <input
                                    type="text"
                                    placeholder="INR"
                                    value={editCurrency}
                                    onChange={(e) => setEditCurrency(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Note (tn)</label>
                            <input
                                type="text"
                                placeholder="e.g. Salon Payment"
                                value={editTransactionNote}
                                onChange={(e) => setEditTransactionNote(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>

                        {/* UPI Link Preview */}
                        {editUpiId && (
                            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <p className="text-xs font-medium text-purple-700 mb-1">UPI Link Preview:</p>
                                <p className="text-xs text-purple-600 break-all">
                                    {generateUpiLink(editUpiId, editPayeeName, editDefaultAmount, editCurrency, editTransactionNote)}
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowEditPaymentMethodModal(false);
                                    setEditingPaymentMethod(null);
                                    setEditPaymentMethodName('');
                                    setEditPaymentMethodStatus('active');
                                    setEditUpiId('');
                                    setEditPayeeName('');
                                    setEditDefaultAmount('');
                                    setEditCurrency('INR');
                                    setEditTransactionNote('');
                                }}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdatePaymentMethod}
                                disabled={isUpdatingPaymentMethod}
                                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isUpdatingPaymentMethod ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Updating...
                                    </>
                                ) : (
                                    'Update'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default KiosQuicksale;
